import { useCurrentUniversalCategory } from '@/features/shop/services/UniversalCategories/hooks'
import {
  getCategoryMetaDescription,
  getParentCategory,
  urlBuilder,
  getUniversalCategories,
} from '@/features/shop/services/UniversalCategories/utils'
import { SEOHead } from '@/features/shared/components/SEOHeader/SEOHead'
import { SEO_PAGE_TYPE } from '@/features/shared/components/SEOHeader/types'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { useMarketingZip } from '@/features/shop/components/UngatedCategoryPage/hooks'
import { useQueryUniversalSearch } from '@/features/shop/services/UniversalCategories/queries'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'
import { useSearchParams } from 'next/navigation'

export const UngatedCategoryHead = () => {
  const universalCategories = getUniversalCategories()

  const pageCategory = useCurrentUniversalCategory()
  const page = useSearchParams()?.get('page')
  const pageTitle = pageCategory?.name ?? 'Categories'
  const parentCategory = getParentCategory(pageCategory)
  const pageDescription = getCategoryMetaDescription(pageCategory)
  const image = {
    url: objectStorageUrl('category-hub-banner-bag.png'),
    width: 645,
    height: 551,
  }
  const canonicalUrl =
    'https://www.shipt.com' + urlBuilder({ universalCategories, category: pageCategory, page })

  const zip = useMarketingZip()
  const { data: productData } = useQueryUniversalSearch(zip ? { zip } : undefined)

  const socialMediaSchema = {
    title: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
    type: SEO_PAGE_TYPE.WEB_PAGE,
    image,
    card: 'summary_large_image',
  }

  const breadcrumbs = {
    '@type': 'BreadcrumbList',
    '@context': 'https://schema.org',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.shipt.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Categories',
        item: 'https://www.shipt.com/categories',
      },
      ...(parentCategory
        ? [
            {
              '@type': 'ListItem',
              position: 3,
              name: parentCategory.name,
              item:
                'https://www.shipt.com' +
                urlBuilder({ universalCategories, category: parentCategory }),
            },
          ]
        : []),
      {
        '@type': 'ListItem',
        position: pageCategory ? 4 : 3,
        name: pageTitle,
        item: 'https://www.shipt.com' + urlBuilder({ universalCategories, category: pageCategory }),
      },
    ],
  }

  const mainEntity = productData
    ? {
        '@type': 'ItemList',
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        itemListElement: productData.products?.map((product) => ({
          '@type': 'Product',
          name: product.name,
          url: `https://www.shipt.com${getProductPageUrl({
            ...product,
            ungated: true,
          })}`,
          image: product.image.url,
          description: product.description,
          offers: {
            '@type': 'Offer',
            price: product.price,
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
          },
        })),
      }
    : undefined

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': [SEO_PAGE_TYPE.COLLECTION_PAGE, SEO_PAGE_TYPE.WEB_PAGE],
    name: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
    image,
    mainEntity,
  }

  return (
    <SEOHead
      title={`${pageTitle} Delivery | Shipt`}
      description={pageDescription}
      canonicalUrl={canonicalUrl}
      image={image}
      breadcrumbs={breadcrumbs}
      socialMediaSchema={socialMediaSchema}
      pageTypeSchema={pageSchema}
    />
  )
}
